import { Outlet, createFileRoute } from '@tanstack/react-router'
import { QrCodeShell } from '@/components/qr/qr-code-shell'
import { fetchSession } from '@/lib/queries/session'
import { currentUser } from '@/lib/auth/session'
import { qrCodeQuery } from '@/lib/queries/qr-codes'

/**
 * `?from=` — which group the user walked down to reach this code.
 *
 * Declared on the parent so the Analytics and Schedules tabs inherit it: the
 * breadcrumb has to survive a tab change, and a tab that dropped the param
 * would silently rewrite the trail on the way in.
 */
export const Route = createFileRoute('/o/$org/qr-codes/$id')({
  validateSearch: (search: Record<string, unknown>): { from?: string } =>
    typeof search.from === 'string' ? { from: search.from } : {},

  loader: async ({ context: { queryClient }, params: { org: slug, id } }) => {
    const user = await currentUser()
    if (!user) return

    /* Already resolved by the org route above, so this is a cache read rather
       than a second round trip — it is needed here only to turn the slug into
       the organization id the query keys are built from. */
    const session = await fetchSession(queryClient, user.id)
    const organization =
      session.organizations.find((candidate) => candidate.slug === slug) ??
      session.organizations[0]

    if (!organization) return

    /* Not awaited — it resolves into the cache the shell suspends on. */
    void queryClient.prefetchQuery(qrCodeQuery(organization.id, id))

    /*
     * The analytics queries are deliberately NOT prefetched here.
     *
     * They are keyed by the viewer's IANA time zone. That was unknowable on the
     * server, which is the reason this comment was written — a server prefetch
     * populated a `UTC` cache entry the browser then ignored in favour of its
     * own, doing the work twice. In the browser the zone IS knowable, so the
     * hazard is gone, but the decision stands on its own merits: these are the
     * Analytics tab's queries and most visits to a code never open it.
     * `QrScanSummary` fetches them when something actually needs them.
     */
  },

  component: QrCodeLayout,
})

function QrCodeLayout() {
  const { id } = Route.useParams()

  return (
    <QrCodeShell constellationId={id}>
      <Outlet />
    </QrCodeShell>
  )
}
